import {useEffect, useEffectEvent, useState} from 'react';
import {Concept} from 'types';
import type {DataLoadStatus} from 'api/types';
import type {Agent, AgentWorkSummary} from 'services/agent/types';
import {useLocalization} from 'localization';
import {useCodelists} from 'services/codelists';
import {useLazySearchAgentWorksQuery} from 'services/elastic/elasticApi';
import {
  mapWorkHitToWorkSummary,
  useMetadataCodelistIds,
} from 'services/metadata';

export const useAgentWorks = (
  agent: Pick<Agent, 'id' | 'roles' | 'agentType'> | undefined,
  page: number,
  size: number,
  doLoad?: boolean,
): {state: DataLoadStatus<AgentWorkSummary[]>; total: number} => {
  const {t} = useLocalization();
  const [state, setState] = useState<DataLoadStatus<AgentWorkSummary[]>>({
    status: 'NotLoaded',
  });
  const onSetState = useEffectEvent(setState);
  const [total, setTotal] = useState<number>(0);
  const onSetTotal = useEffectEvent(setTotal);
  const codelistIds = useMetadataCodelistIds();
  const codelists = useCodelists(codelistIds);

  const [triggerSearchAgentWorks] = useLazySearchAgentWorksQuery();

  useEffect(() => {
    if (!agent?.id || !doLoad) {
      return;
    }

    onSetState(oldState => ({
      ...oldState,
      status: 'Loading',
    }));

    const promise = triggerSearchAgentWorks({agentId: agent.id, page, size});

    promise
      .unwrap()
      .then(result => {
        onSetState({
          status: 'Loaded',
          data: result.hits.map(h => ({
            ...mapWorkHitToWorkSummary(h, codelists),
            roles:
              agent.agentType === Concept.publisher
                ? [t(`general.entity.${Concept.publisher}`)]
                : (agent.roles?.find(r => r.workId === h.id)?.roles ?? []),
          })),
        });
        onSetTotal(result.total);
      })
      .catch(error => {
        onSetState({
          status: 'Failed',
          error: error,
        });
      });
  }, [
    agent?.agentType,
    agent?.id,
    agent?.roles,
    codelists,
    doLoad,
    page,
    size,
    t,
    triggerSearchAgentWorks,
  ]);

  return {
    state,
    total,
  };
};
